import { useQuery } from '@tanstack/react-query'
import { collection, query, where, getDocs, limit } from 'firebase/firestore'
import { useSelector } from 'react-redux'

import { useAuthUser } from './useUser'

import { db } from '@/firebase'

interface OrganizationAccess {
  hasUnlimitedAccess: boolean
  subscriptionId: string | null
  organizationId: string | null
  organizationName: string | null
  subscriptionEndDate: Date | null
  accessType: 'organization' | 'individual' | 'institution_subscriptions'
}

interface InstitutionSubscription {
  id: string
  institutionId: string
  name: string
  plan: string
  endDate: Date
}

interface Organization {
  id: string
  name: string
  members: string[]
  masterSubscriptionStatus: string
  masterSubscriptionId: string
  masterSubscriptionEndDate: Date
}

/**
 * Hook to check if user has organization access with unlimited credits
 * Queries organizations where user is a member and subscription is active
 */
export const useOrganizationAccess = () => {
  const { email } = useAuthUser()

  return useQuery<OrganizationAccess>({
    queryKey: ['organizationAccess', email],
    queryFn: async (): Promise<OrganizationAccess> => {
      if (!email) {
        return {
          hasUnlimitedAccess: false,
          subscriptionId: null,
          organizationId: null,
          organizationName: null,
          subscriptionEndDate: null,
          accessType: 'individual',
        }
      }

      try {
        // Query organizations where user is a member and subscription is active
        const organizationsQuery = query(
          collection(db, 'organizations'),
          where('members', 'array-contains', email),
          where('masterSubscriptionStatus', '==', 'active'),
          limit(1), // We only need one active organization
        )

        const organizationSnapshot = await getDocs(organizationsQuery)

        if (!organizationSnapshot.empty) {
          // User is a member of an organization with active subscription
          const orgDoc = organizationSnapshot.docs[0]
          const orgData = orgDoc.data() as Organization

          return {
            hasUnlimitedAccess: true,
            subscriptionId: orgData?.masterSubscriptionId,
            organizationId: orgDoc.id,
            organizationName: orgData.name || 'Unknown Organization',
            subscriptionEndDate: orgData.masterSubscriptionEndDate || null,
            accessType: 'organization',
          }
        }

        // Check legacy institution subscriptions
        const institutionQuery = query(
          collection(db, 'institution_subscriptions'),
          where('users', 'array-contains', email),
          where('active', '==', true),
          limit(1), // We only need one active institution subscription
        )

        const institutionSnapshot = await getDocs(institutionQuery)

        if (!institutionSnapshot.empty) {
          // User has legacy institution subscription
          const institutionDoc = institutionSnapshot.docs[0]
          const institutionData = institutionDoc.data() as InstitutionSubscription
          const isUnlimited = institutionData?.plan === 'organization' || institutionData?.plan === 'school_starter'

          return {
            hasUnlimitedAccess: isUnlimited,
            subscriptionId: institutionDoc.id,
            organizationId: institutionData?.institutionId,
            organizationName: institutionData.name || institutionData.institutionId || 'Legacy Institution',
            subscriptionEndDate: institutionData.endDate || null,
            accessType: 'organization',
          }
        }

        // No organization or legacy subscription access found
        return {
          hasUnlimitedAccess: false,
          subscriptionId: null,
          organizationId: null,
          organizationName: null,
          subscriptionEndDate: null,
          accessType: 'individual',
        }
      } catch (error) {
        console.error('Error checking organization access:', error)

        // Return individual access on error to be safe
        return {
          hasUnlimitedAccess: false,
          subscriptionId: null,
          organizationId: null,
          organizationName: null,
          subscriptionEndDate: null,
          accessType: 'individual',
        }
      }
    },
    enabled: !!email, // Only run query if we have a user email
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 2, // Retry failed requests twice
  })
}

export default useOrganizationAccess
